<template>
  <b-modal
    :active="active"
    @close="close"
    has-modal-card
    :can-cancel="['escape', 'x', 'outside']"
    :width="1000"
  >
    <div class="modal-card" style="width: 1000px">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ modalTitle }}</p>
      </header>
      <section class="modal-card-body">
        <form @submit.prevent="validateBeforeSubmit">
          <b-notification :closable="false">
            <b-field
              :label="$t('compCoreTagCategoryModal.label-1')"
              :type="{ 'is-danger': errors.has('name') }"
              :message="errors.first('name')"
            >
              <b-input
                name="name"
                expanded
                v-model="ctagCategory.name"
                v-validate="'required|uniqueCategoryName'"
              />
              <p class="control">
                <b-colorpicker
                  v-model="ctagCategory.color"
                  @input="updateColor"
                  append-to-body
                />
              </p>
            </b-field>
            <div class="suggest-colors">
              <b-tooltip
                v-for="(color, index) in suggestColors"
                :key="index"
                :label="$t('compCoreTagCategoryModal.' + color.name)"
                type="is-dark"
                append-to-body
              >
                <a
                  :style="{ 'background-color': color.color }"
                  :data-color="color"
                  @click="presetColor(color.color)"
                >
                  <span
                    v-if="color.color === ctagCategory.color"
                    class="icon is-small"
                    style="
                      color: rgb(255, 255, 255);
                      position: absolute;
                      top: 7px;
                      left: 7px;
                    "
                  >
                    <i class="fa fa-check" />
                  </span>
                </a>
              </b-tooltip>
              <b-field
                style="position: relative; top: -18px"
                :type="{ 'is-danger': true }"
                :message="colorError"
              />
            </div>
          </b-notification>
          <b-notification :closable="false">
            <b-field :label="$t('compCoreTagCategoryModal.label-2')">
              <b-input
                type="textarea"
                :placeholder="$t('compCoreTagCategoryModal.label-2')"
                v-model="ctagCategory.description"
              />
            </b-field>
          </b-notification>
          <b-notification :closable="false">
            <l-field
              :label="$t('compCoreTagCategoryModal.geotag-info')"
              :tooltip="$t('compCoreTagCategoryModal.geotag-tooltip')"
              v-if="useGeoTags"
            >
              <b-switch
                v-model="ctagCategory.isGeoTag"
                :disabled="!canSwitchGeoTag"
              ></b-switch>
            </l-field>

            <div v-if="ctagCategory.isGeoTag" style="margin-bottom: 2ex">
              <geonames-input
                :label="$t('compCoreTagCategoryModal.label-3')"
                :allowed-countries="allowedCountries"
                @select="geoTagSelect"
              />
            </div>

            <b-field
              v-if="!ctagCategory.isGeoTag"
              :label="$t('compCoreTagCategoryModal.label-3')"
            >
              <b-input
                name="tags"
                icon="caret-right"
                expanded
                @input="checkTagsList(true)"
                v-model="newTagName"
              />
              <p class="control">
                <b-button
                  :type="{ 'is-danger': tagsError }"
                  @click="addTag"
                  data-testId="add-tag"
                  class="button"
                  icon-left="plus"
                >
                  {{ $t('add') }}
                </b-button>
              </p>
            </b-field>

            <b-field :type="{ 'is-danger': tagsError }">
              <b-taglist>
                <my-tag
                  v-for="(tag, index) in ctagCategory.tags"
                  :key="index"
                  :tag="tag"
                  :color="ctagCategory.color"
                  :closable="true"
                  :editable="true"
                  @close="removeTag(tag)"
                  @update="checkTagsList"
                />
              </b-taglist>
            </b-field>
            <b-field
              style="position: relative; top: -10px"
              :type="{ 'is-danger': tagsError }"
              :message="tagsError"
            />
          </b-notification>
          <hr />
          <nav class="level">
            <div class="level-left"></div>
            <div class="buttons level-right">
              <b-button
                class="button"
                @click="$emit('close')"
                icon-left="close"
              >
                {{ $t('cancel') }}
              </b-button>
              <button
                v-if="modeCreation"
                type="submit"
                data-testId="submit-tags"
                class="button is-primary"
              >
                <b-icon icon="plus" />
                <span> {{ $t('add') }} </span>
              </button>
              <button v-else type="submit" class="button is-primary">
                <span class="icon is-small"><i class="fa fa-refresh"></i></span>
                <span> {{ $t('update') }} </span>
              </button>
            </div>
          </nav>
        </form>
      </section>
    </div>
  </b-modal>
</template>

<script>
import _ from 'lodash'

import { useAdminStore } from '@/stores/admin.js'
import { useCoreStore } from '@/stores/core.js'
import TagCategory, { Tag } from '@/models/core/TagCategory.js'

import ELECTRICITY_FACTORS from '@/../data/factors/carbon/electricityFactors.json'
import Building from '../../models/carbon/Building'

export default {
  name: 'TagCategoryModal',
  $_veeValidate: {
    validator: 'new'
  },
  props: {
    tagCategory: {
      type: Object,
      default: function () {
        return new TagCategory()
      }
    },
    active: {
      type: Boolean,
      default: false
    },
    modeCreation: {
      type: Boolean,
      default: false
    }
  },
  components: {
    'my-tag': () => import('@/components/core/MyTag.vue'),
    'geonames-input': () => import('@/components/GeonamesInput.vue'),
    'l-field': () => import('@/components/LField.vue')
  },
  setup () {
    const adminStore = useAdminStore()
    const coreStore = useCoreStore()
    return { adminStore, coreStore }
  },
  data () {
    return {
      geoTag: false,
      newTagName: null,
      tagsError: null,
      tagsRemoved: false,
      ctagCategory: _.cloneDeep(this.tagCategory),
      suggestColors: [
        { name: 'cobalt', color: '#273eb2' },
        { name: 'gentian', color: '#5252cc' },
        { name: 'slate', color: '#415df0' },
        { name: 'violet', color: '#8080ff' },
        { name: 'ocean', color: '#0a77a6' },
        { name: 'pacific', color: '#179fd9' },
        { name: 'plum', color: '#a839a4' },
        { name: 'pink', color: '#db60d6' },
        { name: 'strawberry', color: '#ec3e7c' },
        { name: 'cherry', color: '#ba1e55' },
        { name: 'copper', color: '#c44800' },
        { name: 'carrot', color: '#f78400' },
        { name: 'fern', color: '#3cbb3a' },
        { name: 'reef', color: '#1da583' },
        { name: 'forest', color: '#258723' },
        { name: 'pine', color: '#0f735a' },
        { name: 'olive', color: '#b4a40e' },
        { name: 'pickle', color: '#807304' },
        { name: 'sahara', color: '#936d58' },
        { name: 'earth', color: '#54473f' }
      ]
    }
  },
  computed: {
    useGeoTags: function () {
      return this.coreStore.conf.USE_GEOTAGS.value
    },
    allowedCountries: function () {
      // only only the countries for which we have the right EFz
      // for now we only consider electricity
      return Object.keys(ELECTRICITY_FACTORS).map((code) => code.split('.')[0])
    },
    canSwitchGeoTag: function () {
      let nbGeoTags = this.allTagCategories.filter((c) => c.isGeoTag).length
      return (
        // necessary condition (no tag)
        this.ctagCategory.tags.length === 0 &&
        // no geo category yet
        (nbGeoTags === 0 ||
          // the current category is the only geotag
          (nbGeoTags === 1 && this.ctagCategory.isGeoTag))
      )
    },
    modalTitle: function () {
      if (this.modeCreation) {
        return this.$t('compCoreTagCategoryModal.title-1')
      } else {
        return this.$t('compCoreTagCategoryModal.title-2')
      }
    },
    allTagCategories: function () {
      return this.adminStore.allTagCategories
    },
    colorError: function () {
      let error = ''
      let usedColors = this.allTagCategories
        .filter((obj) => !obj.isEqual(this.ctagCategory))
        .map((obj) => obj.color)
      if (usedColors.includes(this.ctagCategory.color)) {
        error = this.$t('compCoreTagCategoryModal.error-4')
      }
      return error
    }
  },
  watch: {
    tagCategory: function () {
      this.ctagCategory = _.cloneDeep(this.tagCategory)
    },
    'ctagCategory.tags': function () {
      this.checkTagsList()
    },
    active: function () {
      if (this.active && !this.tagCategory.color) {
        let usedColors = this.allTagCategories.map((obj) => obj.color)
        let availableColors = this.suggestColors.filter(
          (obj) => !usedColors.includes(obj.color)
        )
        this.ctagCategory.color = availableColors[0].color
      }
    }
  },
  methods: {
    testEfz (efz) {
      // test that we can use this efz code
      // for now only for electricity
      // return null if not found
      return Building.getElectricityFactorCode(efz)
    },
    geoTagSelect (option) {
      this.newTagName = option.name
      this.checkTagsList()
      let efz = option.countryCode + '.' + option.adminCode1
      if (this.testEfz(efz)) {
        // add the tag
        this.addTag({ efz })
      } else {
        // the efz can't be used
        this.tagsError = this.$t('compCoreTagCategoryModal.error-5')
      }
    },
    close () {
      this.newTagName = null
      this.$emit('close')
    },
    updateColor (color) {
      this.ctagCategory.color = color.toString('hex')
    },
    addTag (option) {
      if (!this.tagsError) {
        let tag = new Tag({ name: this.newTagName, ...option })
        this.ctagCategory.addTag(tag)
        this.newTagName = null
      }
    },
    checkTagsList (checkNewTagName = false) {
      this.tagsError = null
      // check within the current category
      let currentTags = this.ctagCategory.tags.map((obj) => obj.name)
      let lengthToCheck = this.ctagCategory.tags.length
      if (checkNewTagName) {
        currentTags.push(this.newTagName)
        lengthToCheck += 1
      }
      if (Array.from(new Set(currentTags)).length === lengthToCheck) {
        // check from other categories
        let allTags = this.adminStore.allTagCategories
          .map((obj) => {
            if (this.ctagCategory.id && this.ctagCategory.id === obj.id) {
              return []
            } else {
              return obj.tags
            }
          })
          .flat()
        for (let usedTag of allTags) {
          for (let tagname of currentTags) {
            if (usedTag.name === tagname) {
              this.tagsError = this.$t('compCoreTagCategoryModal.error-3')
              break
            }
          }
        }
      } else {
        this.tagsError = this.$t('compCoreTagCategoryModal.error-2')
      }
    },
    removeTag (tag) {
      this.ctagCategory.removeTag(tag)
      this.tagsRemoved = true
    },
    presetColor (color) {
      this.ctagCategory.color = color
    },
    save () {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.adminStore
        .updateOrCreateTagCategory(this.ctagCategory)
        .then((data) => {
          loadingComponent.close()
          this.tagsRemoved = false
          this.$emit('close')
        })
        .catch((error) => {
          loadingComponent.close()
          this.tagsRemoved = false
          this.$emit('close')
          throw error
        })
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result && !this.colorError && !this.tagsError) {
          if (this.tagsRemoved) {
            let self = this
            this.$buefy.dialog.confirm({
              title: self.$t('deletion'),
              message: self.$t('compCoreTagCategoryModal.info-1'),
              confirmText: self.$t('validate'),
              cancelText: self.$t('cancel'),
              canCancel: ['escape', 'button', 'outside'],
              onConfirm: function () {
                self.save()
              }
            })
          } else {
            this.save()
          }
        }
      })
    }
  },
  created () {
    let self = this
    this.$validator.extend('uniqueCategoryName', {
      getMessage: (field) => self.$t('compCoreTagCategoryModal.error-1'),
      validate: (value) => {
        return (
          self.allTagCategories.filter((obj) => {
            if (self.ctagCategory.id && self.ctagCategory.id === obj.id) {
              return false
            } else {
              return obj.name === value
            }
          }).length === 0
        )
      }
    })
  },
  mounted () {
    this.ctagCategory.color = '#ff0000'
  }
}
</script>

<style scoped>
.suggest-colors a {
  border-radius: 4px;
  width: 30px;
  height: 30px;
  display: inline-block;
  margin-right: 10px;
  margin-bottom: 10px;
  text-decoration: none;
}
</style>
